import { useEffect, useState } from "react"

import { ConfirmDeleteModal } from "../shared/ConfirmDeleteModal"
import { CelebrationsForm, type CelebrationFormAttributes } from "./CelebrationsForm"
import { CelebrationsList } from "./CelebrationsList"
import { PlaybookGuidance } from "./PlaybookGuidance"
import type {
  StayCelebrationEntry,
  StayCelebrationsData,
  StayCelebrationTraveller,
  StayPlaybookTemplates,
} from "./stayData"
import { VacationOverviewCard } from "./VacationOverviewCard"

type CelebrationsSectionProps = {
  authenticityToken: string
  playbookTemplates: StayPlaybookTemplates
  selectedTravellerId?: string
  vacationId: string
}

export function CelebrationsSection({
  authenticityToken,
  playbookTemplates,
  selectedTravellerId,
  vacationId,
}: CelebrationsSectionProps) {
  const [occasions, setOccasions] = useState<readonly StayCelebrationEntry[]>([])
  const [travellers, setTravellers] = useState<readonly StayCelebrationTraveller[]>([])
  const [error, setError] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [occasionPendingRemoval, setOccasionPendingRemoval] = useState<StayCelebrationEntry | null>(null)
  const isBusy = isLoading || isSaving
  const visibleOccasions = selectedTravellerId
    ? occasions.filter((occasion) => occasion.traveller_ids.includes(selectedTravellerId))
    : occasions

  useEffect(() => {
    async function loadCelebrations() {
      setError(null)
      setIsLoading(true)

      try {
        const data = await fetchVacationSpecialOccasions(vacationId)

        setOccasions(data.occasions)
        setTravellers(data.travellers)
      } catch {
        setError("Celebrations could not be loaded.")
      } finally {
        setIsLoading(false)
      }
    }

    void loadCelebrations()
  }, [vacationId])

  async function addOccasion(attributes: CelebrationFormAttributes) {
    if (isBusy) return false

    setError(null)
    setIsSaving(true)

    try {
      const occasion = await createVacationSpecialOccasion(vacationId, attributes, authenticityToken)

      setOccasions((currentOccasions) => [...currentOccasions, occasion])
      return true
    } catch {
      setError("Celebration could not be added.")
      return false
    } finally {
      setIsSaving(false)
    }
  }

  function openRemoveConfirmation(occasion: StayCelebrationEntry) {
    setOccasionPendingRemoval(occasion)
  }

  function closeRemoveConfirmation() {
    setOccasionPendingRemoval(null)
  }

  async function removeOccasion(occasion: StayCelebrationEntry) {
    setError(null)
    setIsSaving(true)

    try {
      await deleteVacationSpecialOccasion(vacationId, occasion.id, authenticityToken)
      setOccasions((currentOccasions) =>
        currentOccasions.filter((currentOccasion) => currentOccasion.id !== occasion.id),
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <section className="flex min-w-0 flex-col gap-5">
      <h2 className="text-base font-semibold uppercase leading-5 tracking-[0.96px] text-[#525252]">
        Celebrations
      </h2>

      <VacationOverviewCard
        actionExpanded={formOpen}
        actionHasPopup="dialog"
        actionLabel="Add celebration"
        onAction={() => {
          setFormOpen(true)
          setError(null)
        }}
        title="Celebrations"
      >
        <PlaybookGuidance copy={playbookTemplates.celebrations} topic="Celebrations" />

        {formOpen ? (
          <CelebrationsForm
            error={error}
            isBusy={isBusy}
            onAddOccasion={addOccasion}
            onClose={() => {
              if (isSaving) return

              setFormOpen(false)
              setError(null)
            }}
            selectedTravellerId={selectedTravellerId}
            travellers={travellers}
          />
        ) : null}

        {error && !formOpen ? (
          <p className="rounded border border-[#e0b4b4] bg-[#fff2f2] px-3 py-2 text-sm font-bold text-[#873230]">
            {error}
          </p>
        ) : null}

        <CelebrationsList
          isLoading={isLoading}
          isSaving={isSaving}
          occasions={visibleOccasions}
          onRemoveOccasion={openRemoveConfirmation}
          travellers={travellers}
        />
      </VacationOverviewCard>

      {occasionPendingRemoval ? (
        <ConfirmDeleteModal
          errorMessage="Celebration could not be removed."
          message="Are you sure you want to delete this celebration?"
          onClose={closeRemoveConfirmation}
          onConfirm={() => removeOccasion(occasionPendingRemoval)}
          title="Delete celebration"
        />
      ) : null}
    </section>
  )
}

async function fetchVacationSpecialOccasions(vacationId: string) {
  const response = await fetch(`/api/vacations/${vacationId}/vacation_special_occasions`, {
    headers: { Accept: "application/json" },
  })

  if (!response.ok) {
    throw new Error(`Vacation special occasions fetch failed with ${response.status}`)
  }

  return (await response.json()) as StayCelebrationsData
}

async function createVacationSpecialOccasion(
  vacationId: string,
  attributes: CelebrationFormAttributes,
  authenticityToken: string,
) {
  const response = await fetch(`/api/vacations/${vacationId}/vacation_special_occasions`, {
    body: JSON.stringify({ vacation_special_occasion: attributes }),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticityToken,
    },
    method: "POST",
  })

  if (!response.ok) {
    throw new Error(`Vacation special occasion create failed with ${response.status}`)
  }

  return (await response.json()) as StayCelebrationEntry
}

async function deleteVacationSpecialOccasion(
  vacationId: string,
  occasionId: string,
  authenticityToken: string,
) {
  const response = await fetch(`/api/vacations/${vacationId}/vacation_special_occasions/${occasionId}`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticityToken,
    },
    method: "DELETE",
  })

  if (!response.ok) {
    throw new Error(`Vacation special occasion delete failed with ${response.status}`)
  }
}
